import { useState, useMemo, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  Shield,
  Shuffle,
  FlaskConical,
  CalendarDays,
  FileText,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStudyBuilderStore } from "@/stores/study-builder-store";

// ── Node variant styling ────────────────────────────────

type NodeVariant =
  | "root"
  | "eligibility"
  | "inclusion"
  | "exclusion"
  | "randomization"
  | "arm"
  | "visit"
  | "crf";

const VARIANT_STYLES: Record<
  NodeVariant,
  { border: string; icon: LucideIcon; iconColor: string }
> = {
  root:          { border: "border-l-primary",     icon: LayoutDashboard, iconColor: "text-primary" },
  eligibility:   { border: "border-l-amber-500",   icon: Shield,          iconColor: "text-amber-500" },
  inclusion:     { border: "border-l-emerald-500", icon: CheckCircle2,    iconColor: "text-emerald-500" },
  exclusion:     { border: "border-l-red-500",     icon: XCircle,         iconColor: "text-red-500" },
  randomization: { border: "border-l-violet-500",  icon: Shuffle,         iconColor: "text-violet-500" },
  arm:           { border: "border-l-indigo-500",  icon: FlaskConical,    iconColor: "text-indigo-500" },
  visit:         { border: "border-l-teal-500",    icon: CalendarDays,    iconColor: "text-teal-500" },
  crf:           { border: "border-l-sky-500",     icon: FileText,        iconColor: "text-sky-500" },
};

// ── Connector primitives ────────────────────────────────

function Stem({ className }: { className?: string }) {
  return <div className={cn("w-px bg-border", className ?? "h-6")} />;
}

function BranchDown({
  items,
  minChildWidth = 220,
}: {
  items: React.ReactNode[];
  minChildWidth?: number;
}) {
  const count = items.length;
  if (count === 0) return null;

  if (count === 1) {
    return (
      <>
        <Stem />
        {items[0]}
      </>
    );
  }

  return (
    <>
      <Stem className="h-4" />
      <div className="relative flex">
        <div
          className="absolute top-0 h-px bg-border"
          style={{
            left: `${50 / count}%`,
            right: `${50 / count}%`,
          }}
        />
        {items.map((item, i) => (
          <div
            key={i}
            className="flex flex-1 flex-col items-center"
            style={{ minWidth: minChildWidth }}
          >
            <Stem className="h-4" />
            {item}
          </div>
        ))}
      </div>
    </>
  );
}

// ── Flow Card ───────────────────────────────────────────

function FlowCard({
  variant,
  title,
  subtitle,
  badge,
  expandable,
  expanded,
  onToggle,
  children,
}: {
  variant: NodeVariant;
  title: string;
  subtitle?: string;
  badge?: string;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}) {
  const styles = VARIANT_STYLES[variant];
  const Icon = styles.icon;

  return (
    <div className="flex flex-col items-center">
      <div
        role={expandable ? "button" : undefined}
        tabIndex={expandable ? 0 : undefined}
        onClick={expandable ? onToggle : undefined}
        onKeyDown={
          expandable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle?.();
                }
              }
            : undefined
        }
        className={cn(
          "flex w-56 items-center gap-2.5 rounded-lg border border-border border-l-[3px] bg-card px-3 py-2.5 text-left shadow-sm transition-all",
          styles.border,
          expandable && "cursor-pointer hover:bg-muted/50 hover:shadow-md",
          !expandable && "cursor-default",
        )}
      >
        <Icon className={cn("h-4 w-4 shrink-0", styles.iconColor)} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {title}
          </p>
          {subtitle && (
            <p className="truncate text-[11px] text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
        {badge && (
          <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {badge}
          </span>
        )}
        {expandable && (
          <span className="shrink-0 text-muted-foreground">
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Criteria list ───────────────────────────────────────

function CriteriaList({
  items,
}: {
  items: { id: string; number: number; text: string }[];
}) {
  if (items.length === 0) {
    return (
      <p className="py-1 text-center text-[11px] italic text-muted-foreground/60">
        None defined
      </p>
    );
  }

  return (
    <div className="mt-2 flex flex-col items-center gap-1">
      {items.map((c) => (
        <div
          key={c.id}
          className="w-52 rounded border border-border bg-background px-2.5 py-1.5 text-[11px] leading-relaxed text-muted-foreground"
        >
          <span className="font-medium text-foreground">{c.number}.</span>{" "}
          {c.text}
        </div>
      ))}
    </div>
  );
}

// ── CRF chips ───────────────────────────────────────────

function CrfList({
  required,
  optional,
}: {
  required: { id: string; name: string }[];
  optional: { id: string; name: string }[];
}) {
  if (required.length === 0 && optional.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col items-center gap-1">
      {required.map((crf) => (
        <div
          key={crf.id}
          className="flex w-52 items-center gap-2 rounded border border-border bg-background px-2.5 py-1.5"
        >
          <FileText className="h-3 w-3 shrink-0 text-sky-500" />
          <span className="truncate text-[11px] text-foreground">
            {crf.name}
          </span>
          <span className="ml-auto shrink-0 text-[9px] font-bold text-red-500">
            REQ
          </span>
        </div>
      ))}
      {optional.map((crf) => (
        <div
          key={crf.id}
          className="flex w-52 items-center gap-2 rounded border border-dashed border-border bg-background px-2.5 py-1.5"
        >
          <FileText className="h-3 w-3 shrink-0 text-sky-400/70" />
          <span className="truncate text-[11px] text-muted-foreground">
            {crf.name}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Main component ──────────────────────────────────────

export function StudyBuildFlowchart() {
  const build = useStudyBuilderStore((s) => s.build);
  const arms = useStudyBuilderStore((s) => s.arms);
  const visits = useStudyBuilderStore((s) => s.visits);
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const eligibilityCriteria = useStudyBuilderStore((s) => s.eligibilityCriteria);

  const [expandedSet, setExpandedSet] = useState<Set<string>>(
    () => new Set(["eligibility"]),
  );

  const toggle = useCallback((id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isOpen = useCallback(
    (id: string) => expandedSet.has(id),
    [expandedSet],
  );

  const inclusion = useMemo(
    () => eligibilityCriteria.filter((c) => c.type === "inclusion"),
    [eligibilityCriteria],
  );
  const exclusion = useMemo(
    () => eligibilityCriteria.filter((c) => c.type === "exclusion"),
    [eligibilityCriteria],
  );

  const ratioLabel = useMemo(() => {
    const ratios = arms.map((a) => a.randomizationRatio).filter(Boolean);
    if (ratios.length > 0) return ratios[0];
    return arms.length > 1 ? arms.map(() => "1").join(":") : null;
  }, [arms]);

  const getArmVisits = useCallback(
    (armId: string) =>
      visits
        .filter(
          (v) =>
            v.applicableArms.length === 0 ||
            v.applicableArms.includes(armId),
        )
        .sort((a, b) => a.order - b.order),
    [visits],
  );

  const getVisitCrfs = useCallback(
    (visitId: string) => {
      const visit = visits.find((v) => v.id === visitId);
      if (!visit) return { required: [], optional: [] };
      return {
        required: crfs.filter((c) => visit.requiredCrfIds.includes(c.id)),
        optional: crfs.filter((c) => visit.optionalCrfIds.includes(c.id)),
      };
    },
    [visits, crfs],
  );

  if (!build) return null;

  const hasArms = arms.length > 0;
  const multiArm = arms.length > 1;

  return (
    <div className="rounded-lg border border-border bg-card/50">
      <div className="border-b border-border px-4 py-3">
        <h4 className="text-sm font-semibold text-foreground">Study Flow</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Click nodes to expand details
        </p>
      </div>

      <div className="overflow-x-auto p-6">
        <div className="flex min-w-fit flex-col items-center pb-2">
          {/* Study Root */}
          <FlowCard
            variant="root"
            title={build.studyTitle}
            subtitle={`${build.protocolNumber} · Phase ${build.phase}`}
          />

          {/* Eligibility */}
          {eligibilityCriteria.length > 0 && (
            <>
              <Stem />
              <FlowCard
                variant="eligibility"
                title="Eligibility Screening"
                badge={`${eligibilityCriteria.length}`}
                expandable
                expanded={isOpen("eligibility")}
                onToggle={() => toggle("eligibility")}
              >
                {isOpen("eligibility") && (
                  <BranchDown
                    items={[
                      <FlowCard
                        key="inc"
                        variant="inclusion"
                        title="Inclusion"
                        badge={String(inclusion.length)}
                        expandable
                        expanded={isOpen("inclusion")}
                        onToggle={() => toggle("inclusion")}
                      >
                        {isOpen("inclusion") && (
                          <CriteriaList items={inclusion} />
                        )}
                      </FlowCard>,
                      <FlowCard
                        key="exc"
                        variant="exclusion"
                        title="Exclusion"
                        badge={String(exclusion.length)}
                        expandable
                        expanded={isOpen("exclusion")}
                        onToggle={() => toggle("exclusion")}
                      >
                        {isOpen("exclusion") && (
                          <CriteriaList items={exclusion} />
                        )}
                      </FlowCard>,
                    ]}
                  />
                )}
              </FlowCard>
            </>
          )}

          {/* Randomization (multi-arm) */}
          {multiArm && (
            <>
              <Stem />
              <FlowCard
                variant="randomization"
                title="Randomization"
                subtitle={ratioLabel ? `Ratio ${ratioLabel}` : undefined}
                badge={`${arms.length} arms`}
              />
            </>
          )}

          {/* Arms / Visit columns */}
          {hasArms && (
            <BranchDown
              minChildWidth={240}
              items={arms.map((arm) => {
                const armVisits = getArmVisits(arm.id);
                const armKey = `arm:${arm.id}`;
                const armOpen = isOpen(armKey);

                return (
                  <FlowCard
                    key={arm.id}
                    variant="arm"
                    title={arm.name}
                    subtitle={arm.intervention}
                    badge={arm.type.replace("_", " ")}
                    expandable={armVisits.length > 0}
                    expanded={armOpen}
                    onToggle={() => toggle(armKey)}
                  >
                    {armOpen && armVisits.length > 0 && (
                      <div className="flex flex-col items-center">
                        {armVisits.map((visit) => {
                          const vKey = `v:${arm.id}:${visit.id}`;
                          const vOpen = isOpen(vKey);
                          const vCrfs = getVisitCrfs(visit.id);
                          const crfCount =
                            vCrfs.required.length + vCrfs.optional.length;

                          return (
                            <div
                              key={visit.id}
                              className="flex flex-col items-center"
                            >
                              <Stem />
                              <FlowCard
                                variant="visit"
                                title={visit.name}
                                subtitle={
                                  visit.studyDay != null
                                    ? `Day ${visit.studyDay}`
                                    : visit.studyWeek != null
                                      ? `Week ${visit.studyWeek}`
                                      : visit.type.replace("_", " ")
                                }
                                badge={
                                  crfCount > 0
                                    ? `${crfCount} CRFs`
                                    : undefined
                                }
                                expandable={crfCount > 0}
                                expanded={vOpen}
                                onToggle={() => toggle(vKey)}
                              >
                                {vOpen && (
                                  <CrfList
                                    required={vCrfs.required}
                                    optional={vCrfs.optional}
                                  />
                                )}
                              </FlowCard>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </FlowCard>
                );
              })}
            />
          )}

          {/* No arms fallback: show visits directly */}
          {!hasArms && visits.length > 0 && (
            <div className="flex flex-col items-center">
              {visits
                .sort((a, b) => a.order - b.order)
                .map((visit) => {
                  const vKey = `v:${visit.id}`;
                  const vOpen = isOpen(vKey);
                  const vCrfs = getVisitCrfs(visit.id);
                  const crfCount =
                    vCrfs.required.length + vCrfs.optional.length;

                  return (
                    <div
                      key={visit.id}
                      className="flex flex-col items-center"
                    >
                      <Stem />
                      <FlowCard
                        variant="visit"
                        title={visit.name}
                        subtitle={
                          visit.studyDay != null
                            ? `Day ${visit.studyDay}`
                            : visit.studyWeek != null
                              ? `Week ${visit.studyWeek}`
                              : visit.type.replace("_", " ")
                        }
                        badge={
                          crfCount > 0 ? `${crfCount} CRFs` : undefined
                        }
                        expandable={crfCount > 0}
                        expanded={vOpen}
                        onToggle={() => toggle(vKey)}
                      >
                        {vOpen && (
                          <CrfList
                            required={vCrfs.required}
                            optional={vCrfs.optional}
                          />
                        )}
                      </FlowCard>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
